import React, { useMemo } from 'react';
import { format } from 'date-fns';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { CREW_ASSIGNMENTS } from '../../data/projectsData';
import { TODAY, departmentColor, jobById } from '../../utils/projectsUtils';

interface CrewCalendarProps {
  days: Date[];
  activeDepts: string[];
}

// Person-centric companion to CalendarOverview: one row per technician with at least one
// assignment, columns are days, and each cell shows how many jobs that tech is booked on.
const CrewCalendar: React.FC<CrewCalendarProps> = ({ days, activeDepts }) => {
  const isoDays = useMemo(() => days.map((day) => format(day, 'yyyy-MM-dd')), [days]);
  const windowStart = isoDays[0];
  const windowEnd = isoDays[isoDays.length - 1];

  // Assignments on jobs in the active departments (CREW_ASSIGNMENTS is a live binding that
  // swaps when live data loads — recompute on that swap).
  const visibleAssignments = useMemo(
    () =>
      CREW_ASSIGNMENTS.filter((assignment) => {
        const job = jobById(assignment.job_id);
        return job && activeDepts.includes(job.dept_id);
      }),
    [activeDepts, CREW_ASSIGNMENTS],
  );

  // Technicians with at least one assignment, sorted by name.
  const techs = useMemo(() => {
    const map = new Map<string, { emp_id: string; name: string; total: number }>();
    visibleAssignments.forEach((assignment) => {
      if (!assignment.emp_id) return;
      const entry = map.get(assignment.emp_id) || {
        emp_id: assignment.emp_id,
        name: assignment.tech_name || assignment.emp_id,
        total: 0,
      };
      entry.total += 1;
      map.set(assignment.emp_id, entry);
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [visibleAssignments]);

  // Per tech+day: count, department color (from the day's first job), and labels for the tooltip.
  const cellData = useMemo(() => {
    const map = new Map<string, { count: number; color: string; labels: string[] }>();
    visibleAssignments.forEach((assignment) => {
      if (!assignment.date || assignment.date < windowStart || assignment.date > windowEnd) return;
      const key = `${assignment.emp_id}|${assignment.date}`;
      const job = jobById(assignment.job_id);
      const entry = map.get(key) || { count: 0, color: job ? departmentColor(job.dept_id) : '#546e7a', labels: [] };
      entry.count += 1;
      const order = assignment.order_no ? ` · #${assignment.order_no}` : '';
      entry.labels.push(`${assignment.job_id}${order} (${assignment.start_time}, ${assignment.duration}h)`);
      map.set(key, entry);
    });
    return map;
  }, [visibleAssignments, windowStart, windowEnd]);

  const gridTemplate = `220px repeat(${days.length}, minmax(34px, 1fr))`;
  const techDaysInView = Array.from(cellData.values()).reduce((sum, cell) => sum + cell.count, 0);

  return (
    <Card variant="outlined" sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 720 }}>
        {/* Day header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: gridTemplate, position: 'sticky', top: 0, zIndex: 2 }}>
          <Box sx={{ p: 1, backgroundColor: 'grey.200', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="caption" fontWeight={700}>
              Technician
            </Typography>
          </Box>
          {days.map((day) => {
            const iso = format(day, 'yyyy-MM-dd');
            const isToday = iso === TODAY;
            const isWeekendDay = [0, 6].includes(day.getDay());
            return (
              <Box
                key={iso}
                sx={{
                  p: 0.5,
                  textAlign: 'center',
                  backgroundColor: isToday ? 'primary.100' : isWeekendDay ? 'grey.100' : 'grey.200',
                  borderBottom: 1,
                  borderLeft: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="caption" sx={{ fontSize: 10, display: 'block', lineHeight: 1.1 }}>
                  {format(day, 'EEE')}
                </Typography>
                <Typography variant="caption" fontWeight={isToday ? 700 : 500} sx={{ fontSize: 11 }}>
                  {format(day, 'M/d')}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Group strip */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1,
            py: 0.75,
            backgroundColor: 'grey.50',
            borderBottom: 1,
            borderTop: 3,
            borderTopColor: 'primary.main',
            borderBottomColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Technicians
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {techs.length} with assignments · {techDaysInView} tech-days in view
          </Typography>
        </Box>

        {techs.map((tech) => (
          <Box key={tech.emp_id} sx={{ display: 'grid', gridTemplateColumns: gridTemplate }}>
            <Box
              sx={{ p: 0.75, borderBottom: 1, borderColor: 'divider', borderLeft: 4, borderLeftColor: 'primary.main' }}
            >
              <Typography variant="caption" fontWeight={700} sx={{ display: 'block', lineHeight: 1.2 }}>
                {tech.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                {tech.emp_id} · {tech.total} total
              </Typography>
            </Box>
            {isoDays.map((iso) => {
              const cell = cellData.get(`${tech.emp_id}|${iso}`);
              const count = cell?.count || 0;
              const intensity = count === 0 ? 0 : Math.min(1, 0.35 + count * 0.13);
              return (
                <Box
                  key={iso}
                  sx={{
                    borderBottom: 1,
                    borderLeft: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 30,
                  }}
                >
                  {count > 0 && (
                    <Tooltip
                      arrow
                      title={
                        <Box>
                          <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }}>
                            {tech.name} · {format(new Date(`${iso}T00:00:00`), 'EEE MMM d')}
                          </Typography>
                          {cell!.labels.map((label, index) => (
                            <Typography key={index} variant="caption" sx={{ display: 'block' }}>
                              {label}
                            </Typography>
                          ))}
                        </Box>
                      }
                    >
                      <Box
                        sx={{
                          width: '85%',
                          minHeight: 20,
                          borderRadius: 0.5,
                          backgroundColor: cell!.color,
                          opacity: intensity,
                          color: 'white',
                          fontSize: 11,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {count}
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}

        {techs.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No technicians have assignments in the selected departments.
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default CrewCalendar;
